import type { ColDef } from 'ag-grid-community';

export interface NgrColumnConfig {
  member: string;
  label: string;
  customField?: boolean;
  fieldType?: string;
}

/**
 * Column definitions for a Cube result set.
 *
 * Cube returns FLAT rows keyed by dotted member names — `{"bills.count": 8}`.
 * AG Grid reads a dotted `field` as a nested path (`row.bills.count`), so
 * every cell resolved to undefined: the grid rendered the right columns and
 * the right number of rows, all blank. Caught in dev QA 2026-08-16.
 *
 * Bind with `colId` + an explicit `valueGetter` so the literal key is used.
 * Every Cube-fed grid must build its columns here — do not hand-roll
 * `{ field }` from Cube keys again.
 */
export function cubeColumnDefs(rows: Record<string, unknown>[]): ColDef[] {
  if (!rows || rows.length === 0) return [];
  return Object.keys(rows[0]).map((key) => ({
    colId: key,
    headerName: key.split('.').pop(),
    valueGetter: (params: { data?: Record<string, unknown> }) => params.data?.[key],
  }));
}

/**
 * Column definitions for a report whose columns are configured server-side
 * (standard-report presets). Same dotted-key binding rule as above, but the
 * header comes from the preset's label — including custom fields, whose label
 * is the organization's own field name.
 *
 * A custom-field column with no data anywhere in the result is dropped rather
 * than rendered empty: a preset appends every applicable definition, and PRD
 * §5.7 preserves legacy's "only render fields that have data" behavior.
 * Non-custom columns are always shown, so an empty report still has structure.
 */
export function presetColumnDefs(
  columns: NgrColumnConfig[],
  rows: Record<string, unknown>[]
): ColDef[] {
  const hasValue = (member: string) =>
    rows.some((r) => r[member] !== null && r[member] !== undefined && r[member] !== '');

  return columns
    .filter((c) => !c.customField || hasValue(c.member))
    .map((c) => ({
      colId: c.member,
      headerName: c.label,
      valueGetter: (params: { data?: Record<string, unknown> }) => params.data?.[c.member],
    }));
}
